import { useCallback, useEffect, useRef, useState } from 'react'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from 'config/firebase'
import { useAuth } from 'hooks/auth/useAuth'
import { normalizeSidebarPins, toggleSidebarPin } from 'router/sidebarPins'

const STORAGE_PREFIX = 'sidebar-pins:v1:'

function storageKey(uid: string): string {
  return `${STORAGE_PREFIX}${uid}`
}

function readLocalPins(uid: string): string[] | null {
  try {
    const raw = localStorage.getItem(storageKey(uid))
    if (raw == null) return null
    return normalizeSidebarPins(JSON.parse(raw) as unknown)
  } catch {
    return null
  }
}

function writeLocalPins(uid: string, pins: string[]): void {
  try {
    localStorage.setItem(storageKey(uid), JSON.stringify(pins))
  } catch {
    // Private mode or quota. Firestore remains the cross-device copy.
  }
}

export function useSidebarPins(): {
  pins: string[]
  togglePin: (id: string) => void
} {
  const { currentUser, userInfo } = useAuth()
  const uid = currentUser?.uid
  const [pins, setPins] = useState<string[]>([])
  const pinsRef = useRef(pins)
  pinsRef.current = pins
  const hydratedFor = useRef<string | null>(null)

  useEffect(() => {
    if (!uid) {
      hydratedFor.current = null
      pinsRef.current = []
      setPins([])
      return
    }
    if (!userInfo) return
    if (userInfo.id && userInfo.id !== uid) return
    if (hydratedFor.current === uid) return
    hydratedFor.current = uid
    const server = Array.isArray(userInfo.sidebar_pins)
      ? normalizeSidebarPins(userInfo.sidebar_pins)
      : null
    const next = server ?? readLocalPins(uid) ?? []
    pinsRef.current = next
    setPins(next)
  }, [uid, userInfo])

  const togglePin = useCallback(
    (id: string) => {
      if (!uid) return
      const next = toggleSidebarPin(pinsRef.current, id)
      pinsRef.current = next
      setPins(next)
      writeLocalPins(uid, next)
      void updateDoc(doc(db, 'users', uid), { sidebar_pins: next }).catch(error => {
        console.error('Failed to save sidebar pins', error)
      })
    },
    [uid],
  )

  return { pins, togglePin }
}
